import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';

import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { type DevDocsBylineConnectionAncestor as DevDocsBylineConnectionAncestorType } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import { BylineConnectionsPopup } from './BylineConnectionsPopup.tsx';
import { BylineConnectionsPlaceholder } from './ssr/BylineConnectionsPlaceholder.tsx';
import { MAX_BYLINE_CONNECTION_ICONS } from './utils/getBylineConnectionIcons.ts';
import { useBylineConnectionsData } from './hooks/useBylineConnectionsData.ts';
import { useBylineConnectionsInteraction } from './hooks/useBylineConnectionsInteraction.ts';
import { useBylineConnectionsVisibility } from './hooks/useBylineConnectionsVisibility.ts';
import { useBylineConnectionsViewedEvent } from './hooks/useBylineConnectionsViewedEvent.ts';
import { useBylineConnectionsLoadTiming } from './hooks/useBylineConnectionsLoadTiming.ts';
import { useBylineTwgResultEvents } from './hooks/useBylineTwgResultEvents.ts';
import { useTeamworkGraphPreviewPanel } from './teamwork-graph-preview-panel/useTeamworkGraphPreviewPanel.tsx';
import type { BylineContentType as BylineContentTypeType } from './utils/contentAri.ts';

export type BylineConnectionsProps = {
	contentId: string;
	/**
	 * Pages and blog posts are distinct Teamwork Graph node types, so every graph query and the
	 * At-a-glance request are keyed on this.
	 */
	contentType: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
};

export type DevDocsBylineConnectionAncestor = DevDocsBylineConnectionAncestorType;
export type BylineContentType = BylineContentTypeType;

const BylineConnectionsInner: FC<BylineConnectionsProps> = (props) => {
	const { contentId, contentType } = props;
	const { title, error: pageTitleError } = usePageTitle({ contentId });
	const { isTeamworkGraphPreviewPanelOpen, openTeamworkGraphPreviewPanel } =
		useTeamworkGraphPreviewPanel(contentId, contentType);
	const {
		isOpen,
		atAGlanceEnabled,
		triggerRef,
		handleClose: closeMenu,
		handleNavigate,
		handleOpenPreviewPanel,
		handleToggle: toggleMenu,
		handlePlaceholderOpenChange,
	} = useBylineConnectionsInteraction({
		contentId,
		isTeamworkGraphPreviewPanelOpen,
		openTeamworkGraphPreviewPanel,
	});
	const data = useBylineConnectionsData({
		...props,
		pageTitle: pageTitleError ? null : title,
		atAGlanceEnabled,
	});
	const { outgoing, incoming, relatedContent, devDocsConnection } = data;
	// Embedded Confluence has no preview panel. Keep gate evaluation short-circuited.
	const isEmbeddedMenu =
		useIsEmbeddedConfluence_DO_NOT_USE() &&
		(isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment') ||
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			fg('migrate_planner_to_docs_tab'));
	const handleClose = useCallback(() => closeMenu(isEmbeddedMenu), [closeMenu, isEmbeddedMenu]);
	const visibility = useBylineConnectionsVisibility({
		contentId,
		contentType,
		isEmbeddedMenu,
		data,
	});
	const hasOutboundConnection = outgoing.links.length > 0 || devDocsConnection !== null;
	useBylineConnectionsLoadTiming(
		contentId,
		visibility.isResolving,
		visibility.hasConnection,
		outgoing.error || (!hasOutboundConnection ? incoming.error : null),
	);
	useBylineConnectionsViewedEvent(visibility);
	const fireTwgResultActioned = useBylineTwgResultEvents({ visibility, data });
	const handleToggle = useCallback(
		() => toggleMenu(fireTwgResultActioned),
		[toggleMenu, fireTwgResultActioned],
	);

	const { links: outgoingLinks, refreshLinkUrl } = outgoing;
	const {
		links: incomingLinks,
		twgLinks: incomingTwgLinks,
		hasNextPage: incomingLinksHaveMore,
		settled: incomingLinksSettled,
	} = incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = relatedContent;
	const { devDocsIcons } = visibility;
	const menuProps = useMemo(
		() => ({
			isEmbeddedMenu,
			links: outgoingLinks,
			incomingLinks,
			incomingLinksHaveMore,
			incomingLinksSettled,
			additionalIcons: devDocsIcons,
			relatedContentLinks,
			relatedContentLoading,
			devDocsConnection,
			contentId,
			refreshLinkUrl,
			onNavigate: handleNavigate,
			onOpenPreviewPanel: handleOpenPreviewPanel,
		}),
		[
			isEmbeddedMenu,
			outgoingLinks,
			incomingLinks,
			incomingLinksHaveMore,
			incomingLinksSettled,
			devDocsIcons,
			relatedContentLinks,
			relatedContentLoading,
			devDocsConnection,
			contentId,
			refreshLinkUrl,
			handleNavigate,
			handleOpenPreviewPanel,
		],
	);
	const triggerProps = useMemo(
		() => ({
			links: outgoingLinks,
			incomingLinks: incomingTwgLinks,
			additionalIcons: devDocsIcons,
			minIcons: MAX_BYLINE_CONNECTION_ICONS,
			triggerRef,
			onToggle: handleToggle,
		}),
		[outgoingLinks, incomingTwgLinks, devDocsIcons, triggerRef, handleToggle],
	);

	if (visibility.hiddenBySsr) return null;
	// Loaded visibility is provisional while queries resolve; the placeholder checks SSR eligibility.
	if (visibility.isResolving) {
		return (
			<BylineConnectionsPlaceholder
				contentId={contentId}
				contentType={contentType}
				connectionAncestors={props.connectionAncestors}
				isOpen={isOpen}
				onOpenChange={handlePlaceholderOpenChange}
			/>
		);
	}
	if (!visibility.shouldRenderByline) return null;

	return (
		<BylineConnectionsPopup
			isOpen={isOpen}
			onClose={handleClose}
			menuProps={menuProps}
			triggerProps={triggerProps}
		/>
	);
};

const BylineConnectionsWithSuspense: FC<BylineConnectionsProps> = (props) => (
	<TransparentErrorBoundary key={props.contentId} attribution={Attribution.INTEGRATIONS}>
		<RelaySuspense
			interactionName="BylinePageAdfQuery"
			fallback={
				<BylineConnectionsPlaceholder
					contentId={props.contentId}
					contentType={props.contentType}
					connectionAncestors={props.connectionAncestors}
				/>
			}
		>
			<BylineConnectionsInner {...props} />
		</RelaySuspense>
	</TransparentErrorBoundary>
);

export const BylineConnections: (props: any) => React.JSX.Element = withSmartCardProviderContext(
	BylineConnectionsWithSuspense,
);
